import { useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import Key from '../../../assets/icons/Key';
import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import useStudentSsoPolicy from '../../../hooks/useStudentSsoPolicy';
import { DropdownButtonItem } from '../../_atoms/DropdownButton/DropdownButton';

/**
 * Per-lesson "require students to sign in" toggle for the live-game settings
 * menu, backed by `game.settings.requireStudentSso`.
 *
 * Reads the org policy and the license itself rather than taking them as props,
 * so the parent's memoised settings menu doesn't have to thread them through.
 *
 * Three things can lock it, checked in this order:
 *  - the org has `enforceStudentSsoForAllLessons` on — sign-in is mandatory and
 *    evaluated at join time, so the switch shows on and locked; a teacher cannot
 *    opt a lesson out.
 *  - the access badge hasn't loaded (or failed), so the plan is unknown rather
 *    than absent. Locked, but with the neutral copy — not the upgrade nag.
 *  - the teacher isn't on the School plan. This mirrors the designer's lesson
 *    settings gate: both surfaces write `settings.requireStudentSso`, so they
 *    have to agree on who may change it.
 *
 * The last two only gate turning it *on* — see the comment on `locked`.
 *
 * Renders through `DropdownButtonItem` so the icon, title and description match
 * the rest of the settings menu exactly. That atom gained `switchDisabled` and
 * `switchColor` for this row: its whole row toggles on click, so a lock has to be
 * honoured there and not only on the switch.
 */
function RequireStudentSsoRow({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  const { t } = useTranslate();
  const { accessBadge, isFetched: isBadgeFetched } = useAccessBadge();
  // From the access badge, so a teacher who isn't an org admin sees the same lock
  // their students will hit at join time. This used to come from the admin-only
  // settings endpoint, which meant Members always got the unlocked default.
  const { orgEnforcesStudentSsoForAllLessons: lockedByOrg } = useStudentSsoPolicy();
  // Same check as ClassroomSection in the designer's lesson settings, including
  // the loading case: until the badge lands the plan is unknown rather than
  // absent, so hold the neutral copy instead of telling a School plan teacher to
  // upgrade for a beat.
  const isSchoolPlan =
    accessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;
  // A *failed* badge request leaves the plan unknown too: `isFetched` is true but
  // there's no badge, which is indistinguishable from freemium if we only check
  // `isFetched`. Both mean "we don't know", so both hold the neutral copy.
  const isPlanUnknown = !isBadgeFetched || !accessBadge;
  const showUpgrade = !isSchoolPlan && !isPlanUnknown;
  // Clearing the requirement is never gated: neither the plan nor a badge we
  // couldn't read should be able to strand a lesson with sign-in forced on and no
  // control to remove it. Only turning it *on* needs the School plan and a
  // resolved badge; org enforcement still overrides both. Mirrors
  // ClassroomSection — both surfaces write settings.requireStudentSso.
  const locked = lockedByOrg || (!value && (isPlanUnknown || !isSchoolPlan));

  return (
    <Tooltip
      title={
        lockedByOrg
          ? t(
              'require-student-sso-locked-tooltip',
              'Locked by your organization settings',
            )
          : showUpgrade
          ? t('require-student-sso-plan-tooltip', 'Available on the School plan')
          : ''
      }
    >
      {/* The wrapper gives the tooltip a hoverable target while the switch inside
          is disabled, and stops the click from reaching the antd menu item — the
          inner row has already handled the toggle by the time it bubbles here, so
          the menu stays open instead of closing on every flip. */}
      <RowSlot onClick={e => e.stopPropagation()}>
        <DropdownButtonItem
          isDarkTheme
          icon={<Key color="currentColor" />}
          title={t('Require Student SSO', 'Require Student SSO')}
          description={
            lockedByOrg
              ? t(
                  'require-student-sso-locked',
                  'Your organization requires students to sign in for every lesson, so this cannot be turned off here.',
                )
              : showUpgrade
              ? t(
                  'require-student-sso-upgrade',
                  'Requiring students to sign in with your school account is part of the School plan.',
                )
              : t(
                  'require-student-sso-desc',
                  'Students must sign in with your school account before they can join.',
                )
          }
          includeSwitch
          switchValue={lockedByOrg || value}
          switchDisabled={locked}
          switchColor={theme.colors.orange}
          onSwitchToggled={onChange}
        />
      </RowSlot>
    </Tooltip>
  );
}

export default RequireStudentSsoRow;

const RowSlot = styled.div`
  display: block;
`;
